import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import Header from './components/Header';
import SelectionToolbar from './components/SelectionToolbar';
import ChartSection from './components/ChartSection';
import TrialTable from './components/TrialTable';
import SocRegimenView from './components/soc/SocRegimenView';
import ReportOrder from './components/ReportOrder';
import FDAApprovalsView from './components/FDAApprovalsView';
import NewsfeedView from './components/NewsfeedView';
import OmnichannelView from './components/OmnichannelView';
import ClinicalPublicationsView from './components/ClinicalPublicationsView';
import ConferenceAbstractsView from './components/ConferenceAbstractsView';
import AssetsView from './components/AssetsView';
import AssetRadarPage from './components/assetRadar/AssetRadarPage';
import EmergingLandscapesView from './components/EmergingLandscapesView';
import TrialTrackerView from './components/TrialTrackerView';
import Aug2026ReadoutTrackerView from './components/Aug2026ReadoutTrackerView';
import CatalystCalendarsView from './components/CatalystCalendarsView';
import OngoingTrialsView from './components/OngoingTrialsView';
import EmergingAssetPage from './components/EmergingAssetPage';
import EmergingCompanyAssetsPage from './components/EmergingCompanyAssetsPage';
import EmergingTrialPage from './components/EmergingTrialPage';
import CatalystCalendar from './components/CatalystCalendar';
import CatalystTrackerPage from './components/catalyst-tracker/CatalystTrackerPage';
import PrimeReportsView from './components/PrimeReportsView';
import PrimeReportDetailView from './components/PrimeReportDetailView';
import ChatbotPage from './components/ChatbotPage';
import IntegratedIndicationDashboard from './components/IntegratedIndicationDashboard';
import SuperuserRoute from './components/superuser/SuperuserRoute';
import SuperuserLayout from './components/superuser/SuperuserLayout';
import SuperuserLegacyFrame from './components/superuser/SuperuserLegacyFrame';
import SuperuserOnboarding from './components/superuser/SuperuserOnboarding';
import ManageEmergingAssets from './components/superuser/ManageEmergingAssets';
import ManageEmergingIndications from './components/superuser/ManageEmergingIndications';
import ManageTrialTrackerSuggestions from './components/superuser/ManageTrialTrackerSuggestions';
import ManageEmergingLandscapes from './components/superuser/ManageEmergingLandscapes';
import ManageCatalystCalendars from './components/superuser/ManageCatalystCalendars';
import ManageOngoingTrials from './components/superuser/ManageOngoingTrials';
import ManageReadoutCompanies from './components/superuser/ManageReadoutCompanies';
import SuperuserDashboard from './components/superuser/SuperuserDashboard';
import SuperuserOrganizationsPage from './components/superuser/SuperuserOrganizationsPage';
import SuperuserPrimeReportsPage from './components/superuser/SuperuserPrimeReportsPage';
import Login from './components/Login';
import Verify2FA from './components/Verify2FA';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import AccountSettings from './components/AccountSettings';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import WaitlistView from './components/WaitlistView';
import HomeLandingView from './components/HomeLandingView';
import AcademicQuestionnaire from './components/AcademicQuestionnaire';
// Default authenticated landing after login is /newsfeed (no /dashboard page).
import { Logo } from './components/Logo';
import { apiService } from './services/api';
import { Trial, IndicationInfo, SocRegimenGroup } from './types';
import { buildRegimenGroupsFromTrials } from './utils/socRegimenGroups';
import './utils/debug';

interface TrialViewProps {
  showReportsView?: boolean;
  initialIndicationCode?: string;
  initialNccnType?: string;
}

const TrialView: React.FC<TrialViewProps> = ({ showReportsView = false, initialIndicationCode, initialNccnType }) => {
  const isReportsPage = showReportsView;
  const navigate = useNavigate();
  const [selectedTrialIds, setSelectedTrialIds] = useState<string[]>([]);
  const [trials, setTrials] = useState<Trial[]>([]);
  const [indicationInfo, setIndicationInfo] = useState<IndicationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasData, setHasData] = useState(false);

  // Selection state
  const [selectedCancerType, setSelectedCancerType] = useState<string>('');
  const [selectedIndication, setSelectedIndication] = useState<string>('');
  const [selectedNccnType, setSelectedNccnType] = useState<string>('nccn');
  const [socStatusFilter, setSocStatusFilter] = useState<'All' | Trial['preferredStatus']>('All');
  const [socGuidelineFilter, setSocGuidelineFilter] = useState<'nccn' | 'non-nccn'>('nccn');
  const [socReportMode, setSocReportMode] = useState<'standard' | 'regimens'>('regimens');
  const [regimenGroups, setRegimenGroups] = useState<SocRegimenGroup[]>([]);

  // Load data when selections are made
  const loadData = async (_cancerType: string, indicationCode: string, nccnType: string, skipOrder = false) => {
    if (!indicationCode || !nccnType) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (!skipOrder) {
        try {
          const reportResult = await apiService.generateReport({
            indication_code: indicationCode,
            nccn_type: nccnType,
          });
          if (reportResult?.url) {
            navigate(reportResult.url);
            return;
          }
          console.log('Report order created successfully - logged in transactions and ledger:', reportResult);
        } catch (orderError: any) {
          if (orderError?.response?.status === 200 || orderError?.response?.data?.url || orderError?.response?.data?.message) {
            const existingUrl = orderError?.response?.data?.url;
            if (existingUrl) {
              navigate(existingUrl);
              return;
            }
            console.log('Report already exists for organization, loading data...');
          } else if (orderError?.response?.status === 429) {
            const rateLimitError = orderError.response.data?.error || 'Rate limit exceeded. Data may still load if available.';
            setError(rateLimitError);
          } else {
            console.warn('Report order creation warning:', orderError);
          }
        }
      }

      // Fetch indication info
      const indInfo = await apiService.fetchIndicationInfo({
        indication_code: indicationCode,
        nccn_type: nccnType,
      });

      // Get nccn_version from indication info or use default
      const nccnVersion = indInfo.nccnVersion || '2.2025';

      setIndicationInfo({
        ...indInfo,
        nccnVersion: nccnVersion,
      });
      setSelectedIndication(indicationCode);
      setSelectedNccnType(nccnType);
      if (indInfo.cancerType) {
        setSelectedCancerType(indInfo.cancerType);
      }

      const fetchParams = {
        indication_code: indicationCode,
        nccn_type: nccnType,
        nccn_version: nccnVersion,
      };

      const { trials: data, regimenGroups: regimenFromTrials } = await apiService.fetchTrials(fetchParams);

      let resolvedRegimenGroups = regimenFromTrials;
      if (resolvedRegimenGroups.length === 0) {
        try {
          resolvedRegimenGroups = await apiService.fetchSocRegimenGroups(fetchParams);
        } catch (regimenErr) {
          console.warn('fetchSocRegimenGroups failed, using trial fallback if available:', regimenErr);
        }
      }
      if (resolvedRegimenGroups.length === 0 && data.length > 0) {
        resolvedRegimenGroups = buildRegimenGroupsFromTrials(data);
      }
      setRegimenGroups(resolvedRegimenGroups);

      const hasTrials = data.length > 0;
      const hasRegimens = resolvedRegimenGroups.length > 0;
      console.log(
        `Loaded ${data.length} trials, ${resolvedRegimenGroups.length} regimen groups for indication: ${indicationCode}`,
      );

      if (!hasTrials && !hasRegimens) {
        setError(`No data found for this indication with nccn_type="${nccnType}" and nccn_version="${nccnVersion}".`);
        setHasData(false);
        setTrials([]);
      } else {
        setTrials(data);
        setHasData(true);
        if (!hasTrials && hasRegimens) {
          setError(null);
        }
      }
    } catch (err: any) {
      console.error('Error fetching data:', err);
      let errorMessage = 'Failed to load trial data';
      if (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error') {
        errorMessage = 'Network Error: Cannot connect to backend. Please ensure Flask is running on port 5000.';
      } else if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setTrials([]);
      setRegimenGroups([]);
      setHasData(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialIndicationCode && initialNccnType) {
      loadData(selectedCancerType, initialIndicationCode, initialNccnType, true);
    }
  }, [initialIndicationCode, initialNccnType]);

  useEffect(() => {
    setSelectedTrialIds((prev) => prev.filter((id) => trials.some((t) => t.id === id)));
  }, [trials]);

  const handleToolbarChange = (cancerType: string, indication: string, nccnType: string) => {
    setSelectedCancerType(cancerType);
    setSelectedIndication(indication);
    setSelectedNccnType(nccnType);

    // Only load if all selections are made - don't clear data if incomplete
    // This allows users to change selections without losing current view
    if (cancerType && indication && nccnType) {
      loadData(cancerType, indication, nccnType);
    }
    // Don't clear data if selections are incomplete - keep current view
  };

  const toggleSelection = (id: string) => {
    setSelectedTrialIds((prev) =>
      prev.includes(id) ? prev.filter((tid) => tid !== id) : [...prev, id],
    );
  };

  const fdaSearch = useMemo(() => {
    const cancerType = (indicationInfo?.cancerType || selectedCancerType || '').trim();
    if (!cancerType) return { query: '', label: '' };
    const normalized = cancerType.toLowerCase();
    if (normalized.includes('pancreatic adenocarcinoma') || normalized === 'pdac') {
      return { query: 'pancreatic adenocarcinoma', label: 'pancreatic adenocarcinoma or PDAC' };
    }
    return { query: cancerType, label: cancerType };
  }, [indicationInfo?.cancerType, selectedCancerType]);

  const uniquePmidCount = useMemo(() => {
    // Regimen list is NCCN-only; Analyst View counts follow the guideline toggle.
    const guidelineForCount =
      socReportMode === 'regimens' ? 'nccn' : socGuidelineFilter;

    if (socReportMode === 'regimens') {
      const fromRegimens = regimenGroups
        .flatMap((g) => g.pmids || []);
      if (fromRegimens.length > 0) return new Set(fromRegimens.filter(Boolean)).size;
    }

    const filteredTrials = trials.filter((t) =>
      guidelineForCount === 'nccn'
        ? t.preferredStatus !== 'N/A'
        : t.preferredStatus === 'N/A',
    );
    const fromTrials = filteredTrials.map((t) => t.pmid).filter(Boolean);
    if (fromTrials.length > 0) return new Set(fromTrials).size;
    return filteredTrials.length;
  }, [regimenGroups, trials, socGuidelineFilter, socReportMode]);

  return (
    <div className="min-h-screen text-slate-900 pb-12 relative bg-slate-50 font-sans">
      <Header />

      {(isReportsPage || hasData) && (
        <SelectionToolbar
          selectedCancerType={selectedCancerType}
          selectedIndication={selectedIndication}
          selectedNccnType={selectedNccnType}
          onSelectionChange={handleToolbarChange}
          loading={loading}
          indicationInfo={hasData ? indicationInfo : null}
          uniquePmidCount={uniquePmidCount}
        />
      )}

      <main className="w-full max-w-[100%] px-3 sm:px-4 lg:px-6 py-3 space-y-3">
        {!isReportsPage && !hasData && !loading && !selectedCancerType && !selectedIndication && (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8 space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">What would you like to learn about today?</h2>
              <p className="text-sm text-slate-500 mt-2">Report types below.</p>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Report Library</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Link
                  to="/reports"
                  className="px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                >
                  <div className="font-semibold">Standard of Care Reports</div>
                  <div className="text-xs text-slate-500 font-normal mt-1">
                    Compare guideline-backed regimens and trial outcomes.
                  </div>
                </Link>
                <Link
                  to="/emerging-landscapes"
                  className="px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                >
                  <div className="font-semibold">Landscapes & Ongoing Trials</div>
                  <div className="text-xs text-slate-500 font-normal mt-1">
                    Competitive matrices and ongoing trial timelines by disease and line.
                  </div>
                </Link>
                <Link
                  to="/emerging-calendars"
                  className="px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                >
                  <div className="font-semibold">Catalyst Calendars</div>
                  <div className="text-xs text-slate-500 font-normal mt-1">
                    Indication catalyst tables linked to emerging trials.
                  </div>
                </Link>
                <Link
                  to="/conference-abstracts"
                  className="px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                >
                  <div className="font-semibold">Conference Abstracts</div>
                  <div className="text-xs text-slate-500 font-normal mt-1">
                    Browse Ruby Preferred conference abstracts by meeting.
                  </div>
                </Link>
                <Link
                  to="/fda-approvals"
                  className="px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                >
                  <div className="font-semibold">FDA Approvals</div>
                  <div className="text-xs text-slate-500 font-normal mt-1">
                    Review the latest oncology approvals and labels.
                  </div>
                </Link>
                <Link
                  to="/clinical-publications"
                  className="px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                >
                  <div className="font-semibold">Journal Publications</div>
                  <div className="text-xs text-slate-500 font-normal mt-1">
                    Browse recent clinical oncology publications.
                  </div>
                </Link>
                <Link
                  to="/assets"
                  className="px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                >
                  <div className="font-semibold">Assets</div>
                  <div className="text-xs text-slate-500 font-normal mt-1">
                    Query assets by molecular target and modality.
                  </div>
                </Link>
                <Link
                  to="/prime-reports"
                  className="px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                >
                  <div className="font-semibold">Archived Reports</div>
                  <div className="text-xs text-slate-500 font-normal mt-1">
                    Older curated reports kept for reference.
                  </div>
                </Link>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-8">
            <p className="text-slate-500 text-sm">Loading trial data...</p>
          </div>
        )}

        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-rose-700 text-sm whitespace-pre-line">
            {error}
          </div>
        )}

        {!loading && !error && hasData && (
          <>
            <div className="flex items-center justify-center sm:justify-start">
              <div className="inline-flex items-center gap-1 bg-white rounded-xl border border-gray-100 shadow-card p-1">
                <button
                  type="button"
                  onClick={() => setSocReportMode('regimens')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    socReportMode === 'regimens'
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  NCCN Regimens
                </button>
                <button
                  type="button"
                  onClick={() => setSocReportMode('standard')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    socReportMode === 'standard'
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Analyst View
                </button>
              </div>
            </div>

            {socReportMode === 'standard' ? (
              <>
                <section aria-label="Data Visualization" className="animate-fadeIn">
                  <ChartSection
                    data={trials}
                    selectedTrialIds={selectedTrialIds}
                    indicationName={indicationInfo?.name}
                    statusFilter={socStatusFilter}
                    onStatusFilterChange={setSocStatusFilter}
                    guidelineFilter={socGuidelineFilter}
                    onGuidelineFilterChange={setSocGuidelineFilter}
                  />
                </section>

                <section aria-label="Trial Data" className="animate-fadeIn">
                  <TrialTable
                    data={trials}
                    selectedIds={selectedTrialIds}
                    onToggleSelection={toggleSelection}
                    indicationName={indicationInfo?.name}
                    statusFilter={socStatusFilter}
                    guidelineFilter={socGuidelineFilter}
                  />
                </section>
              </>
            ) : (
              <section aria-label="NCCN regimens" className="animate-fadeIn">
                <SocRegimenView
                  groups={regimenGroups}
                  loading={loading}
                  indicationName={indicationInfo?.name}
                  statusFilter={socStatusFilter}
                  onStatusFilterChange={setSocStatusFilter}
                />
              </section>
            )}
          </>
        )}

        {!loading && !error && hasData && indicationInfo && (
          <section className="bg-white rounded-2xl shadow-card border border-gray-100 px-4 py-3">
            <div className="text-sm text-slate-700">
              <span className="font-semibold text-slate-900">
                Would you like to review FDA approvals for this indication?
              </span>{' '}
              <Link
                to={`/fda-approvals${fdaSearch.query ? `?q=${encodeURIComponent(fdaSearch.query)}` : ''}`}
                target="_blank"
                rel="noreferrer"
                className="text-indigo-600 hover:text-indigo-700 font-semibold"
              >
                Search FDA approvals{fdaSearch.label ? ` for ${fdaSearch.label}` : ''}
              </Link>
            </div>
          </section>
        )}
      </main>

      <footer className="border-t border-slate-200/60 mt-10 py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 flex flex-col items-center text-center">
          <div className="mb-4 opacity-80 grayscale hover:grayscale-0 transition-all duration-500">
            <Logo showText={true} />
          </div>

          <div className="flex gap-6 mb-6 text-sm font-medium text-slate-500">
            <a href="#" className="hover:text-indigo-600 transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-indigo-600 transition-colors">
              Terms of Service
            </a>
            <a href="#" className="hover:text-indigo-600 transition-colors">
              Methodology
            </a>
            <a href="#" className="hover:text-indigo-600 transition-colors">
              Contact Support
            </a>
          </div>

          <p className="text-slate-400 text-xs mt-2 max-w-lg mx-auto leading-relaxed">
            © {new Date().getFullYear()} Ruby Oncology Insights. All rights reserved.
            <br />
          </p>
        </div>
      </footer>
    </div>
  );
};

const ReportRouteView: React.FC = () => {
  const { indicationCode, nccnType } = useParams();
  return (
    <TrialView
      showReportsView={true}
      initialIndicationCode={indicationCode}
      initialNccnType={nccnType}
    />
  );
};

/** Deep link: `/unified-dashboard/:indicationCode` loads SoC context without the picker step (NCCN-only). */
const IntegratedDashboardWithParams: React.FC = () => {
  const { indicationCode } = useParams<{ indicationCode: string }>();
  return (
    <IntegratedIndicationDashboard
      variant="standalone"
      initialIndicationCode={indicationCode}
      disableUrlSync
    />
  );
};

/** Old bookmarks used `/unified-dashboard/:code/:nccnType`; strip the redundant segment. */
const UnifiedDashboardLegacyNccnRedirect: React.FC = () => {
  const { indicationCode } = useParams<{ indicationCode: string; legacyNccn: string }>();
  if (!indicationCode) {
    return <Navigate to="/unified-dashboard" replace />;
  }
  return <Navigate to={`/unified-dashboard/${indicationCode}`} replace />;
};

const SuperuserCatchAll: React.FC = () => {
  const location = useLocation();
  const legacyPath = location.pathname;
  const removedPrefixes = [
    '/superuser/emerging-reports',
    '/superuser/fda-links',
    '/superuser/mailerlite',
    '/superuser/crm',
    '/superuser/database',
    '/superuser/catalyst-scraper',
    '/superuser/mailerlite-dashboard',
    '/superuser/database-admin',
  ];
  if (removedPrefixes.some((p) => legacyPath === p || legacyPath.startsWith(`${p}/`))) {
    return <Navigate to="/superuser" replace />;
  }
  return (
    <SuperuserLayout>
      <SuperuserLegacyFrame title="Superuser Console" legacyPath={legacyPath} />
    </SuperuserLayout>
  );
};


const App: React.FC = () => {
  console.log('App component rendering...');
  
  try {
    return (
      <BrowserRouter>
        <Routes>
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          <Route
            path="/waitlist"
            element={
              <PublicRoute>
                <WaitlistView />
              </PublicRoute>
            }
          />
          <Route 
            path="/reset-request" 
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            } 
          />
          <Route 
            path="/reset-password/:token" 
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            } 
          />
          <Route 
            path="/reset_password/:token" 
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            } 
          />
          <Route 
            path="/verify-2fa-code" 
            element={
              <PublicRoute>
                <Verify2FA />
              </PublicRoute>
            } 
          />
          <Route 
            path="/two-factor-verify" 
            element={
              <PublicRoute>
                <Verify2FA />
              </PublicRoute>
            } 
          />
          <Route 
            path="/reports" 
            element={
              <ProtectedRoute>
                <ReportOrder />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/academic-questionnaire"
            element={
              <ProtectedRoute>
                <AcademicQuestionnaire />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/reports/:indicationCode/:nccnType" 
            element={
              <ProtectedRoute>
                <ReportRouteView />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/" 
            element={
              <PublicRoute>
                <HomeLandingView />
              </PublicRoute>
            } 
          />
          <Route
            path="/dashboard"
            element={<Navigate to="/newsfeed" replace />}
          />
          <Route
            path="/unified-dashboard"
            element={
              <ProtectedRoute>
                <IntegratedIndicationDashboard variant="standalone" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/unified-dashboard/:indicationCode/:legacyNccn"
            element={
              <ProtectedRoute>
                <UnifiedDashboardLegacyNccnRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/unified-dashboard/:indicationCode"
            element={
              <ProtectedRoute>
                <IntegratedDashboardWithParams />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/fda-approvals" 
            element={
              <ProtectedRoute>
                <FDAApprovalsView />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/newsfeed"
            element={
              <ProtectedRoute>
                <NewsfeedView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/omnichannel"
            element={
              <ProtectedRoute>
                <OmnichannelView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clinical-publications"
            element={
              <ProtectedRoute>
                <ClinicalPublicationsView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/conference-abstracts"
            element={
              <ProtectedRoute>
                <ConferenceAbstractsView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assets"
            element={
              <ProtectedRoute>
                <AssetsView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/query-assets"
            element={
              <ProtectedRoute>
                <AssetsView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/asset-radar"
            element={
              <ProtectedRoute>
                <AssetRadarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/asset-radar/:tab"
            element={
              <ProtectedRoute>
                <AssetRadarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emerging-landscapes"
            element={
              <ProtectedRoute>
                <EmergingLandscapesView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emerging-landscapes/:slug"
            element={
              <ProtectedRoute>
                <EmergingLandscapesView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trial-tracker"
            element={
              <ProtectedRoute>
                <TrialTrackerView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aug2026-clin-trial-readout-tracker"
            element={
              <ProtectedRoute>
                <Aug2026ReadoutTrackerView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emerging-calendars"
            element={
              <ProtectedRoute>
                <CatalystCalendarsView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emerging-calendars/:slug"
            element={
              <ProtectedRoute>
                <CatalystCalendarsView />
              </ProtectedRoute>
            }
          />
          {/* Legacy / advanced browse (asset & company). Main entry is Landscapes with Ongoing toggle. */}
          <Route
            path="/ongoing-trials"
            element={
              <ProtectedRoute>
                <OngoingTrialsView />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/emerging-assets/:assetId" 
            element={
              <ProtectedRoute>
                <EmergingAssetPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/emerging-companies/:companyId" 
            element={
              <ProtectedRoute>
                <EmergingCompanyAssetsPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/trials/:trialId"
            element={
              <ProtectedRoute>
                <EmergingTrialPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/prime-reports" 
            element={
              <ProtectedRoute>
                <PrimeReportsView />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/prime-reports/:reportId" 
            element={
              <ProtectedRoute>
                <PrimeReportDetailView />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/chatbot"
            element={
              <ProtectedRoute>
                <ChatbotPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/catalysts"
            element={
              <ProtectedRoute>
                <div className="min-h-screen text-slate-900 pb-20 relative bg-slate-50 font-sans">
                  <Header />
                  <CatalystCalendar />
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/catalyst-tracker"
            element={
              <ProtectedRoute>
                <CatalystTrackerPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/account" 
            element={
              <ProtectedRoute>
                <div className="min-h-screen text-slate-900 pb-20 relative bg-slate-50 font-sans">
                  <Header />
                  <AccountSettings />
                </div>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/superuser"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <SuperuserDashboard />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/email-alerts"
            element={<Navigate to="/superuser/organizations" replace />}
          />
          <Route
            path="/superuser/onboarding"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <SuperuserOnboarding />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/organizations"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <SuperuserOrganizationsPage />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/users"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <SuperuserLegacyFrame title="Users" legacyPath="/superuser/create-user" />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/subscriptions"
            element={<Navigate to="/superuser/organizations" replace />}
          />
          <Route
            path="/superuser/subscription-manager"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <SuperuserLegacyFrame title="Subscriptions (legacy)" legacyPath="/superuser/subscriptions" />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/reports"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <SuperuserLegacyFrame title="All Reports" legacyPath="/superuser/all-reports" />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/emerging-reports"
            element={<Navigate to="/superuser" replace />}
          />
          <Route
            path="/superuser/prime-reports"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <SuperuserPrimeReportsPage />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/create-prime-report"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <SuperuserLegacyFrame title="Create Prime Report" legacyPath="/superuser/create-prime-report" />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/assets"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <ManageEmergingAssets />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/indications"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <ManageEmergingIndications />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/trial-tracker-suggestions"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <ManageTrialTrackerSuggestions />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/ongoing-trials"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <ManageOngoingTrials />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/landscapes"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <ManageEmergingLandscapes />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/calendars"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <ManageCatalystCalendars />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/fda-links"
            element={<Navigate to="/superuser" replace />}
          />
          <Route
            path="/superuser/readout-companies"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <ManageReadoutCompanies />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/mailerlite"
            element={<Navigate to="/superuser" replace />}
          />
          <Route
            path="/superuser/crm"
            element={<Navigate to="/superuser" replace />}
          />
          <Route
            path="/superuser/database"
            element={<Navigate to="/superuser" replace />}
          />
          <Route
            path="/superuser/activity"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <SuperuserLegacyFrame title="User Activity" legacyPath="/superuser/user-activity-monitor" />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/engagement"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserLayout>
                    <SuperuserLegacyFrame title="Client Engagement" legacyPath="/superuser/client-engagement" />
                  </SuperuserLayout>
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser/catalyst-scraper"
            element={<Navigate to="/superuser" replace />}
          />
          <Route
            path="/superuser/*"
            element={
              <ProtectedRoute>
                <SuperuserRoute>
                  <SuperuserCatchAll />
                </SuperuserRoute>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    );
  } catch (error) {
    console.error('Error in App component:', error);
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error Loading App</h1>
          <p className="text-gray-700">{String(error)}</p>
        </div>
      </div>
    );
  }
};

export default App;
